import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, Eye, Armchair, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const tests = {
  eye: {
    icon: <Eye className="w-10 h-10" strokeWidth={1.5} />,
    title: "Eye Personality Test",
    titleHi: "आँखों का Personality Test",
    description: "Your eyes are the window to your soul. The shape, color, and expression of your eyes reveal deep-seated personality traits that even you might not be aware of.",
    descHi: "आपकी आँखें आपकी आत्मा का दर्पण हैं। आपकी आँखों का आकार, रंग और भाव आपके गहरे व्यक्तित्व गुणों को प्रकट करते हैं।",
    traits: [
      { type: "Round Eyes", trait: "Creative, Emotional, Empathetic", traitHi: "रचनात्मक, भावनात्मक, सहानुभूतिपूर्ण" },
      { type: "Almond Eyes", trait: "Balanced, Observant, Mysterious", traitHi: "संतुलित, चौकस, रहस्यमय" },
      { type: "Hooded Eyes", trait: "Analytical, Deep Thinker, Wise", traitHi: "विश्लेषणात्मक, गहरा विचारक, बुद्धिमान" },
      { type: "Wide Eyes", trait: "Adventurous, Open-minded, Curious", traitHi: "साहसी, खुले विचारों वाला, जिज्ञासु" },
    ],
    cta: "Get your detailed eye personality reading from our Astrologer",
    ctaHi: "हमारे ज्योतिषी से अपनी आँखों का विस्तृत personality reading पाएं",
  },
  sitting: {
    icon: <Armchair className="w-10 h-10" strokeWidth={1.5} />,
    title: "Sitting Style Personality",
    titleHi: "बैठने की शैली Personality",
    description: "The way you sit reveals subconscious patterns about your personality. Your body language speaks volumes about who you truly are on the inside.",
    descHi: "जिस तरह से आप बैठते हैं, वह आपके अवचेतन व्यक्तित्व पैटर्न को प्रकट करता है।",
    traits: [
      { type: "Cross-legged", trait: "Creative, Free-spirited, Flexible", traitHi: "रचनात्मक, स्वतंत्र, लचीला" },
      { type: "Legs Together", trait: "Disciplined, Organized, Reliable", traitHi: "अनुशासित, व्यवस्थित, विश्वसनीय" },
      { type: "Ankle Cross", trait: "Elegant, Composed, Diplomatic", traitHi: "सुरुचिपूर्ण, शांत, कूटनीतिक" },
      { type: "Legs Apart", trait: "Confident, Dominant, Leader", traitHi: "आत्मविश्वासी, प्रभावशाली, नेता" },
    ],
    cta: "Discover your deeper personality secrets through an astrology reading",
    ctaHi: "ज्योतिष reading के माध्यम से अपने गहरे personality रहस्य जानें",
  },
  "birth-month": {
    icon: <Calendar className="w-10 h-10" strokeWidth={1.5} />,
    title: "Birth Month Personality",
    titleHi: "जन्म महीने का Personality",
    description: "The month you were born holds cosmic energy that shapes your entire personality. Each birth month carries unique celestial vibrations.",
    descHi: "जिस महीने में आपका जन्म हुआ, उसमें cosmic ऊर्जा है जो आपके पूरे व्यक्तित्व को आकार देती है।",
    traits: [
      { type: "Jan-Mar", trait: "Ambitious, Determined, Natural Leaders", traitHi: "महत्वाकांक्षी, दृढ़, प्राकृतिक नेता" },
      { type: "Apr-Jun", trait: "Creative, Social, Energetic", traitHi: "रचनात्मक, सामाजिक, ऊर्जावान" },
      { type: "Jul-Sep", trait: "Analytical, Loyal, Detail-oriented", traitHi: "विश्लेषणात्मक, वफादार, विस्तार-उन्मुख" },
      { type: "Oct-Dec", trait: "Passionate, Intuitive, Adventurous", traitHi: "भावुक, सहज ज्ञानी, साहसी" },
    ],
    cta: "Get your complete birth month reading from our Astrologer",
    ctaHi: "हमारे ज्योतिषी से अपना पूरा जन्म महीने का reading पाएं",
  }
};

export default function PersonalityTestPage() {
  const { testType } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const test = tests[testType];

  if (!test) {
    return (
      <div className="min-h-screen bg-cosmos flex items-center justify-center">
        <p className="text-slate-400 font-body">Test not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmos" data-testid={`personality-test-${testType}`}>
      <header className="glass-card border-b border-white/10 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-slate-400 hover:text-white hover:bg-white/5">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <h1 className="font-heading text-lg font-bold gold-text">
            {language === "hi" ? test.titleHi : test.title}
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center">
          <div className="text-gold mx-auto mb-6">{test.icon}</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold gold-text mb-4">
            {language === "hi" ? test.titleHi : test.title}
          </h2>
          <p className="text-slate-400 font-body text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {language === "hi" ? test.descHi : test.description}
          </p>
        </motion.div>

        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {test.traits.map((item, i) => (
            <motion.div key={i} variants={fadeInUp}
              className="glass-card rounded-2xl p-6 hover:border-gold/30 transition-all duration-500"
              data-testid={`trait-card-${i}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-semibold text-white">{item.type}</h3>
              </div>
              <p className="text-slate-300 font-body text-sm leading-relaxed">
                {language === "hi" ? item.traitHi : item.trait}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="glass-card rounded-2xl p-8 text-center border-gold/20">
          <Sparkles className="w-8 h-8 text-gold mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-slate-300 font-body text-base mb-6 max-w-lg mx-auto leading-relaxed">
            {language === "hi" ? test.ctaHi : test.cta}
          </p>
          <Button
            data-testid="test-cta-btn"
            onClick={() => navigate("/reading")}
            className="bg-gold text-black hover:bg-gold-hover font-body font-semibold rounded-full px-8 py-5 text-base gold-glow transition-all duration-300 hover:scale-105"
          >
            {language === "hi" ? "अपनी रीडिंग शुरू करें" : "Start Your Reading"}
            <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
